/**
 * @providesModule PrizeBoxClaimViewV2
 *
 */
// @ts-nocheck
import {Group, LinearGradient, Path, RadialGradient, Shape, Surface} from 'react-art';
import BalanceBar from 'BalanceBar';
import CurrencyController from 'CurrencyController';
import I18n, {_m} from 'INTL';
import Label from 'Label';
import SKZConstants from 'SKZConstants';
import {PrizeBox, PrizeBoxRecord} from 'PrizeBox';
import Sizes from 'Sizes';
import SkillzImage from 'SkillzImage';
import SkillzStyle from 'SkillzStyle';
import Animation from 'lottie-react-native';
import React, {useState, useRef, useEffect} from 'react';
import {
  ActivityIndicator,
  Animated,
  Easing,
  SafeAreaView,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import ReactNativeHapticFeedback from 'react-native-haptic-feedback';
import ReactLinearGradient from 'react-native-linear-gradient';
import {stylesFn} from './PrizeBoxClaimViewV2.style';
import {PrizeBoxClaimViewV2Props} from './PrizeBoxClaimViewV2Props';
import PrizeBoxClaimAnimation from './anim_prize_box_claim_skz_ios.json';
import {SKZUserInterface} from 'SKZUser';

const PRIZE_BOX_OPEN_DURATION = 1580;
const PRIZE_BOX_CURRENCY_APPEARS_DELAY = 1100;
const PRIZE_BOX_MAX_LOOP_DURATION = 3000;

const PRIZE_BOX_TOTAL = 12510;
const PRIZE_BOX_CLOSED_LOOP_START = 1160;
const PRIZE_BOX_OPEN_START = 5400;
const PRIZE_BOX_FINISH_LOOP_START = 7680;

const PRIZE_BOX_SIZE_MULTIPLIER = 1;
const PRIZE_BOX_ANIM_RATIO = 667 / 375;
const PRIZE_BOX_WIDTH = Sizes.select({
  phone: Sizes.screenWidth * PRIZE_BOX_SIZE_MULTIPLIER,
  tablet: Sizes.screenWidth * PRIZE_BOX_SIZE_MULTIPLIER * 0.66,
});
const PRIZE_BOX_HEIGHT = PRIZE_BOX_WIDTH * PRIZE_BOX_ANIM_RATIO;
const PRIZE_BOX_CURRENCY_IMAGE_HEIGHT = PRIZE_BOX_HEIGHT * 0.12;
const PRIZE_BOX_CURRENCY_IMAGE_GRADIENT_CIRCLE = PRIZE_BOX_HEIGHT * 0.28;

const prizeBoxCurrencyContainerStyle = {
  width: PRIZE_BOX_WIDTH * 0.4,
  height: PRIZE_BOX_HEIGHT * 0.37,
  bottom: PRIZE_BOX_HEIGHT * 0.38,
};

type PrizeBoxClaimAnimEnum = 'DROP_IN' | 'LOOP_CLOSED' | 'CLAIM' | 'LOOP_OPENED';

type ExtendedTransform =
  | {translateX: Animated.Value}
  | {translateY: Animated.Value}
  | {scale: Animated.Value};

export const PrizeBoxClaimViewV2 = (props: PrizeBoxClaimViewV2Props) => {
  const [shouldShowAnimation, setShouldShowAnimation] = useState(true);

  const styles = stylesFn();
  const translateY = prizeBoxCurrencyContainerStyle.height - PRIZE_BOX_CURRENCY_IMAGE_HEIGHT;
  const backgroundImage = useRef('');
  const didStartClaim = useRef(false);
  const didStartDropIn = useRef(false);
  const didStartLoopClosedAnim = useRef(false);
  const hasFinishedPrizeBoxOpen = useRef(false);
  const loopTimer = useRef<NodeJS.Timeout | null>(null);
  const prizeBoxClaimed = useRef(false);

  const currencyIconOpacityValue = useRef(new Animated.Value(0));
  const currencyIconScaleValue = useRef(new Animated.Value(0.1));
  const currencyIconTranslateValue = useRef(new Animated.ValueXY({x: 0, y: translateY}));
  const hasAppearedOpacityValue = useRef(new Animated.Value(0));
  const prizeClaimProgress = useRef(new Animated.Value(0));
  const prizeContainerScaleValue = useRef(new Animated.Value(0.3));

  const claimingPrizeBoxOpenAnim = useRef<Animated.CompositeAnimation | null>(null);
  const closedLoopPrizeBoxAnim = useRef<Animated.CompositeAnimation | null>(null);
  const openLoopPrizeBoxAnim = useRef<Animated.CompositeAnimation | null>(null);
  const prizeBoxOpenedLoopAnim = useRef<Animation | null>(null);
  const prizeBoxDropInAnim = useRef<Animated.CompositeAnimation | null>(null);

  const sizingIconStyle = {
    width: PRIZE_BOX_CURRENCY_IMAGE_HEIGHT,
    height: PRIZE_BOX_CURRENCY_IMAGE_HEIGHT,
  };

  const transformValue: ExtendedTransform[] =
    currencyIconTranslateValue.current.getTranslateTransform();

  transformValue.push({
    scale: currencyIconScaleValue.current,
  });

  const prizeText =
    props.loginBonus == undefined
      ? _m('Congratulations!\nYou completed an achievement!')
      : _m('Tap to Open');

  const notchPadding = {
    paddingTop:
      Sizes.isNotchedIphone() && Sizes.isPortrait()
        ? SKZConstants.IPHONEX_NOTCH_HEIGHT_PORTRAIT
        : 0,
  };

  const halfCircle = PRIZE_BOX_CURRENCY_IMAGE_GRADIENT_CIRCLE / 2;

  useEffect(() => {
    startNextAnimation('DROP_IN');
  }, []);

  const getAnimationAccessibilityLabel = (): string => {
    if (props.prizeBox?.prizes?.length > 0) {
      let prizeAccessibilityLabel = '';
      props.prizeBox.prizes.map((prize: PrizeBoxRecord) => {
        const prizeAmount = prize.amount;
        const prizeType = prize.type;
        prizeAccessibilityLabel += I18n.formattedMessage(
          'Prize Awarded {0} {1} ',
          prizeAmount,
          prizeType
        );
      });
      return prizeAccessibilityLabel;
    }
    return '';
  };

  const clearAutoClaimTimer = () => {
    if (loopTimer.current) {
      clearTimeout(loopTimer.current);
      loopTimer.current = null;
    }
  };

  const updatePrizeBoxClaimed = () => {
    clearAutoClaimTimer();

    if (!prizeBoxClaimed.current) {
      prizeBoxClaimed.current = true;

      startNextAnimation('CLAIM');
    } else if (!hasFinishedPrizeBoxOpen.current) {
      hasFinishedPrizeBoxOpen.current = true;

      beginPrizeBoxOpenLoopAnimation();
    } else {
      dismiss();
    }
  };

  const startNextAnimation = (state: PrizeBoxClaimAnimEnum) => {
    if (state == 'DROP_IN') {
      if (!didStartDropIn.current) {
        didStartDropIn.current = true;
        beginPrizeBoxDropInAnimation();
      }
    } else if (state == 'LOOP_CLOSED') {
      if (!didStartLoopClosedAnim.current) {
        didStartLoopClosedAnim.current = true;

        clearAutoClaimTimer();

        loopTimer.current = setTimeout(() => {
          updatePrizeBoxClaimed();
        }, PRIZE_BOX_MAX_LOOP_DURATION);

        beginPrizeBoxClosedLoopAnimation();
      }
    } else if (state == 'CLAIM') {
      didStartClaim.current = true;

      beginPrizeBoxClaimAnimation();
    } else if (state == 'LOOP_OPENED') {
      if (!hasFinishedPrizeBoxOpen.current) {
        hasFinishedPrizeBoxOpen.current = true;

        beginPrizeBoxOpenLoopAnimation();
      }
    }
  };

  const beginPrizeBoxDropInAnimation = () => {
    prizeBoxDropInAnim.current = Animated.timing(prizeClaimProgress.current, {
      toValue: PRIZE_BOX_CLOSED_LOOP_START / PRIZE_BOX_TOTAL,
      duration: PRIZE_BOX_CLOSED_LOOP_START,
      easing: Easing.linear,
      useNativeDriver: true,
    });
    prizeBoxDropInAnim.current.start(() => {
      startNextAnimation('LOOP_CLOSED');
    });
  };

  const beginPrizeBoxClosedLoopAnimation = () => {
    const forceValue = PRIZE_BOX_CLOSED_LOOP_START / PRIZE_BOX_TOTAL;
    prizeClaimProgress.current.setValue(forceValue);
    closedLoopPrizeBoxAnim.current = Animated.timing(prizeClaimProgress.current, {
      toValue: PRIZE_BOX_OPEN_START / PRIZE_BOX_TOTAL,
      duration: PRIZE_BOX_OPEN_START - PRIZE_BOX_CLOSED_LOOP_START,
      easing: Easing.linear,
      useNativeDriver: true,
    });
    closedLoopPrizeBoxAnim.current.start(() => {
      if (!didStartClaim.current) {
        beginPrizeBoxClosedLoopAnimation();
      }
    });
  };

  const beginPrizeBoxClaimAnimation = () => {
    didStartLoopClosedAnim.current = true;
    didStartDropIn.current = true;
    const forceValue = PRIZE_BOX_OPEN_START / PRIZE_BOX_TOTAL;
    prizeClaimProgress.current.setValue(forceValue);
    claimingPrizeBoxOpenAnim.current = Animated.parallel([
      currencyOpacityUpAnim(),
      currencyScaleUpAnim(),
      prizeContainerResizeAnim(),
      currencyTranslateUpAnim(),
      prizeBoxOpenAnim(),
    ]);
    setTimeout(() => {
      ReactNativeHapticFeedback.trigger('impactHeavy', {enableVibrateFallback: true});
      setTimeout(() => {
        ReactNativeHapticFeedback.trigger('impactMedium', {enableVibrateFallback: true});
        setTimeout(() => {
          ReactNativeHapticFeedback.trigger('notificationWarning', {enableVibrateFallback: true});
          setTimeout(() => {
            ReactNativeHapticFeedback.trigger('impactHeavy', {enableVibrateFallback: true});
          }, 400);
        }, 200);
      }, 200);
    }, 400);
    claimingPrizeBoxOpenAnim.current.start(() => {
      startNextAnimation('LOOP_OPENED');
    });
  };

  const beginPrizeBoxOpenLoopAnimation = () => {
    currencyIconOpacityValue.current.setValue(1);
    currencyIconTranslateValue.current.setValue({
      x: 0,
      y: 0,
    });
    currencyIconScaleValue.current.setValue(1);
    prizeContainerScaleValue.current.setValue(1);
    Animated.timing(hasAppearedOpacityValue.current, {
      toValue: 1,
      duration: 0,
      easing: Easing.linear,
      useNativeDriver: true,
    }).start(() => {
      startLoopPrizeBoxOpenAnimation();
    });
  };

  const startLoopPrizeBoxOpenAnimation = () => {
    const forceValue = PRIZE_BOX_FINISH_LOOP_START / PRIZE_BOX_TOTAL;
    prizeClaimProgress.current.setValue(forceValue);
    openLoopPrizeBoxAnim.current = Animated.timing(prizeClaimProgress.current, {
      toValue: 1,
      duration: PRIZE_BOX_TOTAL - PRIZE_BOX_FINISH_LOOP_START,
      easing: Easing.linear,
      useNativeDriver: true,
    });
    openLoopPrizeBoxAnim.current?.start(() => {
      if (shouldShowAnimation) {
        startLoopPrizeBoxOpenAnimation();
      }
    });
  };

  const dismiss = () => {
    setShouldShowAnimation(false);
    props.dismissClaimBox?.();
  };

  const getEasingForCurrencyAnimation = () => {
    return Easing.bezier(0.14, 1.42, 0.45, 1.19);
  };

  const currencyScaleUpAnim = () => {
    return Animated.timing(currencyIconScaleValue.current, {
      toValue: 1,
      duration: PRIZE_BOX_OPEN_DURATION - PRIZE_BOX_CURRENCY_APPEARS_DELAY,
      delay: PRIZE_BOX_CURRENCY_APPEARS_DELAY,
      easing: Easing.linear,
      useNativeDriver: true,
    });
  };

  const prizeContainerResizeAnim = () => {
    return Animated.timing(prizeContainerScaleValue.current, {
      toValue: 1,
      duration: PRIZE_BOX_OPEN_DURATION - PRIZE_BOX_CURRENCY_APPEARS_DELAY,
      delay: PRIZE_BOX_CURRENCY_APPEARS_DELAY,
      easing: Easing.linear,
      useNativeDriver: true,
    });
  };

  const currencyOpacityUpAnim = () => {
    return Animated.timing(currencyIconOpacityValue.current, {
      toValue: 1,
      duration: 0,
      delay: PRIZE_BOX_CURRENCY_APPEARS_DELAY,
      easing: Easing.linear,
      useNativeDriver: true,
    });
  };

  const currencyTranslateUpAnim = () => {
    return Animated.timing(currencyIconTranslateValue.current, {
      toValue: {
        x: 0,
        y: 0,
      },
      duration: PRIZE_BOX_OPEN_DURATION - PRIZE_BOX_CURRENCY_APPEARS_DELAY,
      delay: PRIZE_BOX_CURRENCY_APPEARS_DELAY,
      easing: getEasingForCurrencyAnimation(),
      useNativeDriver: true,
    });
  };

  const prizeBoxOpenAnim = () => {
    return Animated.timing(prizeClaimProgress.current, {
      toValue: PRIZE_BOX_FINISH_LOOP_START / PRIZE_BOX_TOTAL,
      duration: PRIZE_BOX_OPEN_DURATION,
      easing: Easing.linear,
      useNativeDriver: true,
    });
  };

  const formatPrizeString = (prize: PrizeBoxRecord) => {
    if (prize.type == 'TICKETZ') {
      return I18n.abbreviatedNumber(prize.amount);
    } else {
      const truncate = !(prize.type == 'CASH');
      const prepend = prize.type == 'CASH' ? '$' : '';
      return I18n.getCurrencyString(prize.amount, prepend, truncate, true);
    }
  };

  const getCurrencyIconForType = (prize: PrizeBoxRecord) => {
    if (prize.type == 'TICKETZ') {
      return 'ic_ticketz_background.png';
    } else if (prize.type == 'CASH') {
      if (!CurrencyController.isUSD()) {
        return 'ic_prize_box_animation_cash_rupee_skz.png';
      } else {
        return 'ic_prize_box_animation_cash_skz.png';
      }
    } else {
      return SkillzStyle.currencyValues.practiceCurrencyFullIconLargeURI;
    }
  };

  const getColorFilterArray = () => {
    const color = props.prizeBox.color;
    const topColor = {
      layer: '',
      color: color,
    };
    const bottomColor = {
      layer: '',
      color: color,
    };

    topColor.layer = '**.giftBoxLoopTopColor.**';
    bottomColor.layer = '**.giftBoxLoopBaseColor.**';

    return [topColor, bottomColor];
  };

  const getPrizeColor = () => {
    if (props.prizeBox.prizes.every((prize: PrizeBoxRecord) => prize.type == 'TICKETZ')) {
      return SkillzStyle.fullStyles.textColorTicketz.color;
    } else if (props.prizeBox.prizes.every((prize: PrizeBoxRecord) => prize.type == 'CASH')) {
      return SkillzStyle.fullStyles.textColorCash.color;
    } else {
      return SkillzStyle.fullStyles.textColorZ.color;
    }
  };

  const getCirclePath = (centerX: number, centerY: number, radius: number): Path => {
    return new Path()
      .moveTo(centerX, centerY - radius)
      .arc(0, radius * 2, radius)
      .arc(0, radius * -2, radius);
  };

  const getRadialCircleColor = (
    centerX: number,
    centerY: number,
    radius: number
  ): LinearGradient => {
    const color = getPrizeColor();

    const colorArray = {
      0: color,
      1: SkillzStyle.convertToOpacity(color, 0),
    };
    return new RadialGradient(colorArray, centerX, centerY, radius, radius, centerX, centerY);
  };

  return backgroundImage.current ? (
    <SkillzImage
      resizeMode={'cover'}
      style={[StyleSheet.absoluteFill]}
      source={{
        uri: backgroundImage.current,
      }}
    >
      {props.userInfo ? (
        <PrizeBoxClaimViewContentV2
          updatePrizeBoxClaimed={updatePrizeBoxClaimed}
          getAnimationAccessibilityLabel={getAnimationAccessibilityLabel}
          notchPadding={notchPadding}
          userInfo={props.userInfo}
          shouldShowAnimation={shouldShowAnimation}
          setRef={(ref: Animation) => {
            prizeBoxOpenedLoopAnim.current = ref;
          }}
          getColorFilterArray={getColorFilterArray}
          prizeBoxCurrencyContainerStyle={prizeBoxCurrencyContainerStyle}
          prizeBox={props.prizeBox}
          circlePath={getCirclePath(halfCircle, halfCircle, halfCircle)}
          radialCircleColor={getRadialCircleColor(halfCircle, halfCircle, halfCircle)}
          getCurrencyIconForType={getCurrencyIconForType}
          sizingIconStyle={sizingIconStyle}
          formatPrizeString={formatPrizeString}
          prizeText={prizeText}
          prizeClaimProgress={prizeClaimProgress.current}
          hasAppearedOpacityValue={hasAppearedOpacityValue.current}
          prizeContainerScaleValue={prizeContainerScaleValue.current}
          currencyIconOpacityValue={currencyIconOpacityValue.current}
          transformValue={transformValue}
        />
      ) : (
        <View style={[StyleSheet.absoluteFill, styles.emptyBackground]}>
          <ActivityIndicator color={'#ffffff'} size={'large'} style={styles.activityIndicator} />
        </View>
      )}
    </SkillzImage>
  ) : (
    <ReactLinearGradient
      style={[
        {
          flex: 1,
        },
        StyleSheet.absoluteFill,
      ]}
      colors={SkillzStyle.gradients.backgroundViewGradient}
    >
      {props.userInfo ? (
        <PrizeBoxClaimViewContentV2
          updatePrizeBoxClaimed={updatePrizeBoxClaimed}
          getAnimationAccessibilityLabel={getAnimationAccessibilityLabel}
          notchPadding={notchPadding}
          userInfo={props.userInfo}
          shouldShowAnimation={shouldShowAnimation}
          setRef={(ref: Animation) => {
            prizeBoxOpenedLoopAnim.current = ref;
          }}
          getColorFilterArray={getColorFilterArray}
          prizeBoxCurrencyContainerStyle={prizeBoxCurrencyContainerStyle}
          prizeBox={props.prizeBox}
          circlePath={getCirclePath(halfCircle, halfCircle, halfCircle)}
          radialCircleColor={getRadialCircleColor(halfCircle, halfCircle, halfCircle)}
          getCurrencyIconForType={getCurrencyIconForType}
          sizingIconStyle={sizingIconStyle}
          formatPrizeString={formatPrizeString}
          prizeText={prizeText}
          prizeClaimProgress={prizeClaimProgress.current}
          hasAppearedOpacityValue={hasAppearedOpacityValue.current}
          prizeContainerScaleValue={prizeContainerScaleValue.current}
          currencyIconOpacityValue={currencyIconOpacityValue.current}
          transformValue={transformValue}
        />
      ) : (
        <View style={[StyleSheet.absoluteFill, styles.emptyBackground]}>
          <ActivityIndicator color={'#ffffff'} size={'large'} style={styles.activityIndicator} />
        </View>
      )}
    </ReactLinearGradient>
  );
};

interface PrizeBoxClaimViewContentV2Props {
  updatePrizeBoxClaimed: () => void;
  getAnimationAccessibilityLabel: () => string;
  notchPadding: {paddingTop: number};
  userInfo: SKZUserInterface;
  shouldShowAnimation: boolean;
  setRef: (ref: Animation) => void;
  getColorFilterArray: () => {keypath: string; color: string}[];
  prizeBoxCurrencyContainerStyle: {width: number; height: number; bottom: number};
  prizeBox: PrizeBox;
  circlePath: LinearGradient;
  radialCircleColor: LinearGradient;
  getCurrencyIconForType: (prize: PrizeBoxRecord) => string;
  sizingIconStyle: {width: number; height: number};
  formatPrizeString: (prize: PrizeBoxRecord) => string;
  prizeText: string;
  prizeClaimProgress: Animated.Value;
  hasAppearedOpacityValue: Animated.Value;
  prizeContainerScaleValue: Animated.Value;
  currencyIconOpacityValue: Animated.Value;
  transformValue: ExtendedTransform[];
}

function PrizeBoxClaimViewContentV2(props: PrizeBoxClaimViewContentV2Props) {
  const styles = stylesFn();

  return (
    <ReactLinearGradient
      style={[
        {
          flex: 1,
        },
        StyleSheet.absoluteFill,
      ]}
      colors={['rgba(0,0,0,0.95)', 'rgba(0,0,0,0.75)']}
    >
      <SafeAreaView
        style={{
          flex: 0,
        }}
      />
      {/* Top Status bar */}
      <SafeAreaView
        style={{
          flex: 1,
        }}
      >
        <TouchableWithoutFeedback
          style={styles.touchableOpacity}
          onPress={props.updatePrizeBoxClaimed}
          accessible={false}
          accessibilityLabel={props.getAnimationAccessibilityLabel()}
          testID={'prize_box'}
        >
          <View style={StyleSheet.absoluteFill}>
            <Animated.View
              style={[
                styles.balanceBarContainer,
                props.notchPadding,
                {
                  opacity: props.hasAppearedOpacityValue,
                },
              ]}
            >
              <BalanceBar
                cashBalance={props.userInfo?.cash_balance || 0}
                zBalance={props.userInfo?.z_balance || 0}
                ticketzBalance={props.userInfo?.ticketz?.balance || 0}
                style={styles.balanceBar}
                darkThemeOverride={true}
                isTouchable={false}
                accessible={true}
              />
            </Animated.View>
            <View style={[StyleSheet.absoluteFill, styles.prizeBoxClaimContainer]}>
              <View style={styles.prizeBoxClaimInnerContainer}>
                {props.shouldShowAnimation && (
                  <React.Fragment>
                    <Animation
                      autoPlay={false}
                      loop={false}
                      progress={props.prizeClaimProgress}
                      ref={props.setRef}
                      resizeMode={'contain'}
                      hardwareAccelerationAndroid={true}
                      colorFiltersToLayers={props.getColorFilterArray()}
                      source={PrizeBoxClaimAnimation}
                      style={{
                        height: PRIZE_BOX_HEIGHT,
                        width: PRIZE_BOX_WIDTH,
                      }}
                    />
                    <Animated.View
                      style={{
                        ...styles.prizeAnimContainer,
                        ...prizeBoxCurrencyContainerStyle,
                        transform: [
                          {
                            scale: props.prizeContainerScaleValue,
                          },
                        ],
                      }}
                    >
                      {props.prizeBox.prizes?.map((prize: PrizeBoxRecord) => {
                        return (
                          <View
                            style={{...styles.prizeAnimContainerInner}}
                            key={prize.amount + prize.type}
                          >
                            <Animated.View
                              style={[
                                {
                                  width: PRIZE_BOX_CURRENCY_IMAGE_GRADIENT_CIRCLE,
                                  height: PRIZE_BOX_CURRENCY_IMAGE_GRADIENT_CIRCLE,
                                  top: -1 * (PRIZE_BOX_CURRENCY_IMAGE_GRADIENT_CIRCLE * 0.27),
                                },
                                styles.gradientSurfaceContainer,
                                {
                                  opacity: props.hasAppearedOpacityValue,
                                },
                              ]}
                            >
                              <Surface
                                width={PRIZE_BOX_CURRENCY_IMAGE_GRADIENT_CIRCLE}
                                height={PRIZE_BOX_CURRENCY_IMAGE_GRADIENT_CIRCLE}
                                style={{
                                  backgroundColor: 'transparent',
                                }}
                                key={'surface'}
                              >
                                <Group>
                                  <Shape d={props.circlePath} fill={props.radialCircleColor} />
                                </Group>
                              </Surface>
                            </Animated.View>
                            <Animated.View
                              style={{
                                opacity: props.currencyIconOpacityValue,
                                transform: props.transformValue,
                              }}
                            >
                              <SkillzImage
                                source={props.getCurrencyIconForType(prize)}
                                resizeMode={'contain'}
                                style={props.sizingIconStyle}
                              />
                            </Animated.View>
                            <Animated.View
                              style={{
                                opacity: props.hasAppearedOpacityValue,
                              }}
                            >
                              <Label style={styles.prizeLabel} testID={'prize_claimed'}>
                                {props.formatPrizeString(prize)}
                              </Label>
                            </Animated.View>
                          </View>
                        );
                      })}
                    </Animated.View>
                  </React.Fragment>
                )}
              </View>
              <Label numberOfLines={2} style={styles.bottomText}>
                {props.prizeText}
              </Label>
            </View>
          </View>
        </TouchableWithoutFeedback>
      </SafeAreaView>
    </ReactLinearGradient>
  );
}
